import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Chart } from 'chart.js';
import { IDebtorAgeStatistics } from 'src/app/reports/reports-models';
import { UtilsService } from 'src/app/shared/utils.service';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { newId } from 'src/app/shared/utilities';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-debtors-ages-chart',
  templateUrl: './debtors-ages-chart.component.html',
  styleUrls: ['../charts.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule]
})
export class DebtorsAgesChartComponent implements OnChanges, AfterViewInit {

  constructor(private utilsService: UtilsService, private translateService: TranslateService) {
  }

  @Input()
  data: IDebtorAgeStatistics;
  @Input()
  textColor: string;
  @Input()
  fontOptions: any;
  private readonly _canvasId = `debtorAgeCanvas${newId()}`;
  get canvasId() { return this._canvasId; }
  sliceColors = [
    '#58a8b8',
    '#668991',
    this.utilsService.readCssStyleProperty('--clr-svea-2'),
    this.utilsService.readCssStyleProperty('--clr-svea-1'),
    this.utilsService.readCssStyleProperty('--clr-svea-6'),
    '#7a7e80',
  ]

  graph = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: this.sliceColors,
        hoverBackgroundColor: this.sliceColors
      }
    ]
  };

  pieChartCanvas: HTMLCanvasElement;
  pieChart;
  options: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.previousValue) {
      this.pieChart.destroy();
      this.buildChart();
    }
  }

  ngAfterViewInit(): void {
    this.pieChartCanvas = document.getElementById(this.canvasId) as HTMLCanvasElement;

    this.buildChart();
  }

  private buildChart() {
    this.options = {
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: {
            color: this.textColor,
            font: this.fontOptions
          }
        },
        datalabels: {
          formatter: (value, ctx) => {
            const datapoints = ctx.chart.data.datasets[0].data;
            const total = datapoints.reduce((total, datapoint) => total + datapoint, 0);
            const percentage = Math.round(value / total * 100);
            if (percentage == 0 || isNaN(percentage)) {return ''; }
            return percentage + '%';
          },
          color(context) {
            const lightColor = window.getComputedStyle(document.body).getPropertyValue('--clr-white');
            const darkColor = window.getComputedStyle(document.body).getPropertyValue('--clr-text-1');
            const labelColors = [darkColor, lightColor, lightColor, darkColor, darkColor, lightColor];

            const index = context.dataIndex;
            return labelColors[index];
          },
          font: {
            weight: window.getComputedStyle(document.body).getPropertyValue('--fw-bold'),
            size: 14,
            family: 'asap-semibold'
          },
          clamp: true,

        }
      },
      animation: {
        onComplete(animation) {
          const sum = animation.chart.config.data.datasets[0].data.reduce((a, b) => a + b);
          if (sum === 0) {
            document.getElementById('noData').style.display = 'block';
            document.getElementById('canvas').style.display = 'none';
          }
          else {
            document.getElementById('canvas').style.display = 'block';
            document.getElementById('noData').style.display = 'none';
          }
        }
      }
    };

    const properties = Object.keys(this.data);
    const labels = [];
    const graphData = [];
    properties.forEach(prop => {
      labels.push(ProprtyNameToLabel[prop]);
      graphData.push(this.data[prop]);
    });
    this.graph.labels = labels;
    this.graph.datasets[0].data = graphData;

    this.pieChart = new Chart(this.pieChartCanvas, {
      type: 'pie',
      data: this.graph,
      options: this.options,
      plugins: [ChartDataLabels],
    });
  }
}

export enum ProprtyNameToLabel {
  'numberDebtorsAgeUnder30' = '18-29',
  'numberDebtorsAge30' = '30-39',
  'numberDebtorsAge40' = '40-49',
  'numberDebtorsAge50' = '50-59',
  'numberDebtorsAge60' = '60-67',
  'numberDebtorsAgeOver67' = '67+'
}
